import { useEffect, useState, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { track } from '@/lib/analytics';
import { getNextPendingTour } from '@/lib/tours';

export interface TourStep {
  /** value of data-tour attribute used to find the target element */
  target: string;
  titleKey: string;
  descKey: string;
  /** preferred placement; falls back to bottom if not enough room */
  placement?: 'bottom' | 'top' | 'auto';
}

interface CoachMarkProps {
  tourId: string;
  steps: TourStep[];
  /** when true, starts at step 0 even if previously completed */
  forceStart?: boolean;
  onFinish?: () => void;
}

const STORAGE_PREFIX = 'careos_tour_';

export function hasCompletedTour(tourId: string): boolean {
  try {
    return localStorage.getItem(STORAGE_PREFIX + tourId) === '1';
  } catch {
    return false;
  }
}

export function resetTour(tourId: string) {
  try {
    localStorage.removeItem(STORAGE_PREFIX + tourId);
  } catch {
    /* noop */
  }
}

function cacheDone(tourId: string) {
  try {
    localStorage.setItem(STORAGE_PREFIX + tourId, '1');
  } catch {
    /* noop */
  }
}

export function CoachMark({ tourId, steps, forceStart, onFinish }: CoachMarkProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stepIndex, setStepIndex] = useState(0);
  const [active, setActive] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [serverDone, setServerDone] = useState<boolean | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const trackedStartRef = useRef(false);

  // Load server-side progress so tours stay done across devices.
  useEffect(() => {
    if (!user) {
      setServerDone(null);
      return;
    }
    let cancelled = false;
    supabase
      .from('user_tour_progress')
      .select('tour_id, completed_at, skipped_at')
      .eq('user_id', user.id)
      .then(({ data }) => {
        if (cancelled || !data) return;
        // Warm the local cache for every known tour of this user.
        for (const row of data) {
          if (row.completed_at || row.skipped_at) cacheDone(row.tour_id);
        }
        const row = data.find((r) => r.tour_id === tourId);
        setServerDone(!!row && (!!row.completed_at || !!row.skipped_at));
      });
    return () => {
      cancelled = true;
    };
  }, [user, tourId]);

  // Decide whether to start
  useEffect(() => {
    if (forceStart) {
      setStepIndex(0);
      setActive(true);
      return;
    }
    // Wait for the server answer when logged in; fall back to local cache.
    if (user && serverDone === null) return;
    const done = user ? serverDone || hasCompletedTour(tourId) : hasCompletedTour(tourId);
    if (!done) {
      // delay so layout settles
      const id = setTimeout(() => setActive(true), 600);
      return () => clearTimeout(id);
    }
  }, [tourId, forceStart, user, serverDone]);

  const current = steps[stepIndex];

  // Analytics: tour started + step viewed
  useEffect(() => {
    if (!active || !current) return;
    if (!trackedStartRef.current) {
      trackedStartRef.current = true;
      track('tour_started', { tour_id: tourId });
    }
    track('tour_step_viewed', { tour_id: tourId, step_index: stepIndex });
  }, [active, current, tourId, stepIndex]);

  // Track target element position
  useLayoutEffect(() => {
    if (!active || !current) return;

    const update = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${current.target}"]`);
      if (!el) {
        setRect(null);
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // measure after scroll
      requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        setRect(r);
      });
    };

    update();
    const handler = () => update();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    const id = window.setInterval(update, 500);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
      window.clearInterval(id);
    };
  }, [active, current]);

  const persist = useCallback(
    (outcome: 'completed' | 'skipped', lastStep: number) => {
      cacheDone(tourId);
      if (!user) return;
      const now = new Date().toISOString();
      // PostgREST builders are lazy thenables: without .then() nothing is sent.
      void supabase
        .from('user_tour_progress')
        .upsert(
          {
            user_id: user.id,
            tour_id: tourId,
            completed_at: outcome === 'completed' ? now : null,
            skipped_at: outcome === 'skipped' ? now : null,
            last_step_index: lastStep,
          },
          { onConflict: 'user_id,tour_id' },
        )
        .then(({ error }) => {
          if (error) console.error('Failed to persist tour progress:', error);
        });
    },
    [tourId, user],
  );

  if (!active || !current) return null;

  const finish = (outcome: 'completed' | 'skipped') => {
    track(outcome === 'completed' ? 'tour_completed' : 'tour_skipped', {
      tour_id: tourId,
      step_index: stepIndex,
      total_steps: steps.length,
    });
    persist(outcome, stepIndex);
    setActive(false);
    onFinish?.();
  };

  const skip = () => finish('skipped');
  const next = () => {
    if (stepIndex >= steps.length - 1) finish('completed');
    else setStepIndex((i) => i + 1);
  };
  const prev = () => setStepIndex((i) => Math.max(0, i - 1));

  const isLastStep = stepIndex >= steps.length - 1;
  const nextTour = isLastStep
    ? getNextPendingTour(tourId, (id) => hasCompletedTour(id))
    : null;

  // Compute popover position
  const padding = 8;
  let popoverStyle: React.CSSProperties = {
    position: 'fixed',
    left: 16,
    right: 16,
    bottom: 16,
    maxWidth: 420,
    margin: '0 auto',
  };

  if (rect) {
    const placeBelow = rect.bottom + 180 < window.innerHeight;
    if (placeBelow) {
      popoverStyle = {
        position: 'fixed',
        top: rect.bottom + padding,
        left: Math.max(16, Math.min(window.innerWidth - 340, rect.left)),
        width: Math.min(340, window.innerWidth - 32),
      };
    } else {
      popoverStyle = {
        position: 'fixed',
        bottom: window.innerHeight - rect.top + padding,
        left: Math.max(16, Math.min(window.innerWidth - 340, rect.left)),
        width: Math.min(340, window.innerWidth - 32),
      };
    }
  }

  const overlay = (
    <div className="fixed inset-0 z-[100] pointer-events-auto" onClick={skip}>
      {/* A single shadow creates a true cutout, preserving the target's original colors. */}
      {rect ? (
        <div
          className="absolute rounded-lg ring-2 ring-primary pointer-events-none transition-all duration-200"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: '0 0 0 9999px hsl(var(--foreground) / 0.6)',
          }}
          aria-hidden="true"
        />
      ) : (
        <div className="absolute inset-0 bg-foreground/60" />
      )}

      {/* Popover */}
      <div
        ref={popoverRef}
        style={popoverStyle}
        className="pointer-events-auto bg-popover text-popover-foreground border rounded-lg shadow-xl p-4 animate-in fade-in zoom-in-95"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="font-semibold text-sm">{t(current.titleKey)}</h4>
          <button
            onClick={skip}
            className="text-muted-foreground hover:text-foreground"
            aria-label={t('common.cancel')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-3">{t(current.descKey)}</p>
        {isLastStep && nextTour && (
          <p className="text-xs text-primary mb-3">{t('tour.nextTourHint')}</p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
            {stepIndex + 1}/{steps.length}
          </span>
          <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
            {stepIndex > 0 && (
              <Button size="sm" variant="ghost" onClick={prev}>
                <ArrowLeft className="w-3 h-3 mr-1" />
                {t('common.back')}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={skip}>
              {t('tour.skip')}
            </Button>
            <Button size="sm" onClick={next}>
              {isLastStep ? t('common.done') : t('common.next')}
              {!isLastStep && <ArrowRight className="w-3 h-3 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
